import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useMostInViewport
 * Determines which child element is the most visible inside a scrolling container (or the window if no container).
 *
 * @param containerRef - ref to the scrolling container element
 * @param childElements - array of child elements in the order they appear
 * @param edgeThreshold - pixel threshold at which the first / last element is immediately considered active
 *
 * @returns index of the child element that is currently the most in view, or -1 when none are visible
 */
export const useMostInViewport = (
  containerRef: React.RefObject<HTMLElement | null>,
  childElements: (HTMLElement | null)[],
) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Store childElements in a ref so scrollToIndex always has access to current values
  const childElementsRef = useRef(childElements);
  useEffect(() => {
    childElementsRef.current = childElements;
  }, [childElements]);

  // Exposed scrollTo helper - reads from ref to avoid stale closure
  const scrollToIndex = useCallback((index: number) => {
    const el = childElementsRef.current[index];
    if (!el) return;

    el.scrollIntoView({ behavior: 'instant', block: 'center' });
    setActiveIndex(index);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visibleEntry = entries.reduce(
        (prev, current) => {
          const prevRatio = prev?.intersectionRatio || 0;
          const currentRatio = current.intersectionRatio;

          // If intersection ratios are equal, prefer the entry whose center is closest to the container's center (or viewport)
          if (currentRatio === prevRatio) {
            // Get container bounds
            const container = containerRef.current;
            const containerRect = container
              ? container.getBoundingClientRect()
              : {
                  top: 0,
                  bottom: window.innerHeight,
                  height: window.innerHeight,
                };
            const containerCenter =
              containerRect.top + containerRect.height / 2;

            // Get center of both entries
            const prevCenter = prev
              ? prev.boundingClientRect.top + prev.boundingClientRect.height / 2
              : -Infinity;
            const currentCenter =
              current.boundingClientRect.top +
              current.boundingClientRect.height / 2;

            const prevDist = Math.abs(prevCenter - containerCenter);
            const currentDist = Math.abs(currentCenter - containerCenter);

            return currentDist < prevDist ? current : prev;
          }

          return currentRatio > prevRatio ? current : prev;
        },
        null as IntersectionObserverEntry | null,
      );

      if (visibleEntry) {
        const index = childElements.indexOf(visibleEntry.target as HTMLElement);
        setActiveIndex(index);
      }
    });

    childElements.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [containerRef, childElements]);

  return { activeIndex, scrollToIndex };
};
